import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { DatePicker } from '~/components/ui/date-picker';
import { Label } from '~/components/ui/label';

export interface FormDatePickerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  /** react-hook-form control object from `useForm`. */
  control: Control<TFieldValues>;
  /** Registered field name — must match a key in your form schema. */
  name: TName;
  /** Label rendered above the picker. Omit to render without a label. */
  label?: string;
  /** Placeholder shown when no date is selected. */
  placeholder?: string;
  /** Disables the picker. */
  disabled?: boolean;
  /** `data-testid` on the field wrapper (default `${name}-date-picker`); the trigger is the button inside it. */
  testId?: string;
}

/**
 * A Controller-wrapped date picker for use with react-hook-form.
 *
 * Stores the selected value as a `Date` object in the form state.
 * Validation errors from the schema render automatically below the picker.
 *
 * @example
 * ```tsx
 * const { control } = useForm<{ startDate: Date }>();
 *
 * <FormDatePicker
 *   control={control}
 *   name="startDate"
 *   label="From"
 *   placeholder="dd/mm/yyyy"
 * />
 * ```
 */
export function FormDatePicker<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  disabled,
  testId,
}: FormDatePickerProps<TFieldValues, TName>) {
  const resolvedTestId = testId ?? `${name}-date-picker`;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div className="space-y-1.5" data-testid={resolvedTestId}>
          {label !== undefined && (
            <Label htmlFor={name} className="text-sm font-medium">
              {label}
            </Label>
          )}
          <DatePicker
            {...(field.value !== undefined && { value: field.value as Date })}
            onChange={field.onChange}
            {...(placeholder !== undefined && { placeholder })}
            {...(disabled !== undefined && { disabled })}
          />
          {fieldState.error?.message !== undefined && (
            <p
              role="alert"
              className="text-destructive text-xs"
              data-testid={`${resolvedTestId}-error`}
            >
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
